import { useCallback, useEffect, useRef, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import type { APIError } from "simplydo/core/api";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

export const ReadonlyEmail = styled.div`
  border: 1px solid #ccc;
  padding: 10px;
  border-radius: 5px;

  cursor: default;

  display: flex;
  justify-content: center;

  overflow-x: auto;
  max-width: 100%;
  min-width: 0;

  iframe {
    overflow: hidden;
    border: none;
    width: 100%;
    max-width: 100%;
    min-width: min(650px, 100%);

    border: none;
    display: block;
  }
`;

function prepareEmailPreviewHtml(html: string) {
  return html.replace(
    'src="cid:bannerImage"',
    'src="cid:bannerImage" height="150"',
  );
}

type EmailPreviewModalProps = {
  open: boolean;
  onClose: () => void;
  html: string;
  title?: string;
};

export function EmailPreviewModal({
  open,
  onClose,
  html,
  title,
}: EmailPreviewModalProps) {
  const { t } = useTranslation();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const displayTitle = title ?? t("emailPreview.title");

  useEffect(() => {
    if (!frameRef.current || !html) {
      return;
    }

    const iframe = frameRef.current;
    const handleLoad = () => {
      try {
        const iframeDoc =
          iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDoc) {
          return;
        }

        const links = iframeDoc.querySelectorAll("a");
        links.forEach((link) => {
          link.setAttribute("target", "_blank");
          link.setAttribute("rel", "noopener noreferrer");
        });
      } catch {
        // Cross-origin restrictions may prevent access
      }
    };

    iframe.addEventListener("load", handleLoad);
    handleLoad();

    return () => {
      iframe.removeEventListener("load", handleLoad);
    };
  }, [html]);

  if (!open || !html) {
    return null;
  }

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader>{displayTitle}</ModalHeader>
      <ModalContent scrolling>
        <ReadonlyEmail>
          <iframe
            scrolling="no"
            ref={frameRef}
            onLoad={() => {
              if (!frameRef.current?.contentWindow) {
                return;
              }
              frameRef.current.style.height = `${frameRef.current.contentWindow.document.body.scrollHeight}px`;
            }}
            srcDoc={prepareEmailPreviewHtml(html)}
            title={displayTitle}
          />
        </ReadonlyEmail>
      </ModalContent>
      <ModalActions>
        <Button onClick={onClose}>{t("generic.close")}</Button>
      </ModalActions>
    </Modal>
  );
}

type FetchEmailPreview = (
  onSuccess: (html: string) => void,
  onFail: (error: APIError) => void,
) => void;

// oxlint-disable-next-line react/only-export-components
export function useEmailPreview() {
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const loadPreview = useCallback((fetch: FetchEmailPreview) => {
    setLoading(true);
    fetch(
      (previewHtml) => {
        setLoading(false);
        setHtml(previewHtml);
      },
      (err) => {
        setLoading(false);
        toast.error(err.message);
      },
    );
  }, []);

  const closePreview = useCallback(() => {
    setHtml(null);
  }, []);

  return { html, open: html !== null, loading, loadPreview, closePreview };
}
